import { useState, useEffect } from 'react';
import { X, Sparkles, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentAnnouncement } from '../config/featureAnnouncements';

const FeatureAnnouncementBanner = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  // Get the current announcement from config
  const announcement = getCurrentAnnouncement();

  useEffect(() => {
    if (!announcement) {
      setIsVisible(false);
      return;
    }

    // Check if user has dismissed this announcement
    const dismissedAnnouncements = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
    if (!dismissedAnnouncements.includes(announcement.id)) {
      setIsVisible(true);
    }
  }, [announcement]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (!announcement) return;
    
    // Save dismissal to localStorage
    const dismissedAnnouncements = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
    dismissedAnnouncements.push(announcement.id);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(dismissedAnnouncements));
  };

  const handleCTAClick = () => {
    if (announcement?.ctaLink) {
      navigate(announcement.ctaLink);
      handleDismiss();
    }
  };

  const handleFeatureClick = (link) => {
    if (link) {
      navigate(link);
      handleDismiss();
    }
  };

  if (!isVisible || !announcement) return null;

  const getColorClasses = () => {
    switch (announcement.type) {
      case 'success':
        return {
          bg: 'bg-green-50 dark:bg-slate-800',
          border: 'border-green-200 dark:border-green-700',
          text: 'text-green-900 dark:text-green-100',
          icon: 'text-green-600 dark:text-green-400',
          button: 'bg-green-600 hover:bg-green-700 text-white'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50 dark:bg-slate-800',
          border: 'border-yellow-200 dark:border-yellow-700',
          text: 'text-yellow-900 dark:text-yellow-100',
          icon: 'text-yellow-600 dark:text-yellow-400',
          button: 'bg-yellow-600 hover:bg-yellow-700 text-white'
        };
      default: // info
        return {
          bg: 'bg-blue-50 dark:bg-slate-800',
          border: 'border-blue-200 dark:border-blue-700',
          text: 'text-blue-900 dark:text-blue-100',
          icon: 'text-blue-600 dark:text-blue-400',
          button: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
    }
  };

  const colors = getColorClasses();

  return (
    <div className={`${colors.bg} ${colors.border} border-b`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-start justify-between gap-4">
          {/* Icon and Content */}
          <div className="flex items-start gap-3 flex-1">
            <div className={`${colors.icon} mt-0.5`}>
              <Sparkles size={20} />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${colors.text} text-sm mb-1`}>
                {announcement.title}
              </h3>
              <p className={`${colors.text} text-sm mb-2`}>
                {announcement.message}
              </p>
              
              {/* Feature List */}
              {announcement.features && announcement.features.length > 0 && (
                <div className="hidden sm:flex flex-wrap gap-2 mb-2">
                  {announcement.features.map((feature, index) => (
                    <button
                      key={index}
                      onClick={() => handleFeatureClick(feature.link)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${colors.text} bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-slate-600 transition-colors`}
                    >
                      <ChevronRight size={12} />
                      {feature.text}
                    </button>
                  ))}
                </div>
              )}

              {/* CTA Button */}
              {announcement.showCTA && announcement.ctaText && (
                <button
                  onClick={handleCTAClick}
                  className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium ${colors.button} transition-colors`}
                >
                  {announcement.ctaText}
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className={`${colors.text} hover:bg-blue-100 dark:hover:bg-slate-700 transition-colors p-1 rounded hover:bg-black/5 dark:hover:bg-white/5`}
            title="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureAnnouncementBanner;
